import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  IconButton,
  Chip,
  TextField,
  Button,
  Paper,
  Divider,
  Fade,
} from '@mui/material';
import { ExpandMore, Delete, Image, Close } from '@mui/icons-material';
import StockInput from '../StockInput';
import type { Variant, StockLocation } from '../../types/product';
import WholesaleBreaksEditor from './WholesaleBreaksEditor';

interface VariantCardProps {
  variant: Variant;
  index: number;
  allLocations: StockLocation[];
  onUpdate: (index: number, updatedVariant: Variant) => void;
  onDelete: (index: number) => void;
  onImageUpload: (index: number, file: File) => void;
  onImageRemove: (index: number) => void;
  onOpenMedia: (index: number) => void;
}

const VariantCard: React.FC<VariantCardProps> = ({
  variant,
  index,
  allLocations,
  onUpdate,
  onDelete,
  onImageUpload,
  onImageRemove,
  onOpenMedia,
}) => {
  const handleChange = (field: keyof Variant, value: any) => {
    onUpdate(index, { ...variant, [field]: value });
  };

  return (
    <Fade in={true}>
      <Accordion
        sx={{
          mb: 1.5,
          borderRadius: '8px !important',
          border: '1px solid',
          borderColor: 'divider',
          '&:before': { display: 'none' },
          overflow: 'hidden',
          boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
          transition: 'box-shadow 0.2s',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            bgcolor: 'action.hover',
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              gap: 2,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            {variant.imageUrl && (
              <Box
                component="img"
                src={variant.imageUrl}
                alt=""
                sx={{
                  width: 32,
                  height: 32,
                  objectFit: 'contain',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                }}
              />
            )}
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {variant.sku}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {variant.attributes.map((attr) => (
                <Chip
                  key={`${attr.name}-${attr.value}`}
                  label={`${attr.name}: ${attr.value}`}
                  size="small"
                  sx={{
                    bgcolor: 'primary.50',
                    color: 'primary.700',
                    fontWeight: 500,
                  }}
                />
              ))}
            </Box>
            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
              ${variant.price}
            </Typography>
          </Box>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onDelete(index);
            }}
            size="small"
            sx={{
              color: 'error.light',
              '&:hover': { color: 'error.main', bgcolor: 'error.50' },
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Basic Info */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="SKU"
                value={variant.sku}
                onChange={(e) => handleChange('sku', e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Price"
                type="number"
                value={variant.price}
                onChange={(e) => handleChange('price', Number(e.target.value))}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>,
                }}
              />
            </Box>

            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                Wholesale pricing (override)
              </Typography>
              <WholesaleBreaksEditor
                value={variant.wholesale_breaks ?? []}
                onChange={(breaks) => handleChange('wholesale_breaks', breaks)}
                hydrateKey={variant.sku || index}
                compact
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Leave blank to use the product's wholesale pricing. Set breaks here to
                override just this variant.
              </Typography>
            </Box>

            {/* Variant Image */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Variant Image
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1.5,
                }}
              >
                {variant.imageUrl ? (
                  <>
                    <Box
                      component="img"
                      src={variant.imageUrl}
                      alt="Variant"
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: 'contain',
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        p: 0.5,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Current image
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => onImageRemove(index)}
                      startIcon={<Close />}
                    >
                      Remove
                    </Button>
                  </>
                ) : (
                  <>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Image sx={{ color: 'text.disabled' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        No image yet
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Upload a specific image for this variant
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => onOpenMedia(index)}
                    >
                      Choose Image
                    </Button>
                  </>
                )}
              </Paper>
            </Box>

            <Divider />

            {/* Stock Input */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Stock Levels
              </Typography>
              <StockInput
                locations={variant.locations}
                allLocations={allLocations}
                onLocationsChange={(updatedLocations) => {
                  handleChange('locations', updatedLocations);
                }}
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Fade>
  );
};

export default React.memo(VariantCard);